import React, { useState } from 'react';
import { getTechniqueInfo } from '../content/helper-content';
import './NeuroInformedBadge.css';

/**
 * NeuroInformedBadge Component
 *
 * Displays a badge indicating that AI-generated content uses a specific
 * neuro-informed technique. Clicking/hovering shows what the technique means.
 *
 * Usage:
 * <NeuroInformedBadge technique="Spaced Retrieval" />
 * <NeuroInformedBadge /> // Shows generic "LRB Neuro-Informed" badge
 */
const NeuroInformedBadge = ({
  technique = null,
  showTooltip = true,
  size = 'medium', // 'small', 'medium', 'large'
  variant = 'default', // 'default', 'subtle', 'outline'
  className = ''
}) => {
  const [isTooltipVisible, setIsTooltipVisible] = useState(false);

  const techniqueInfo = technique ? getTechniqueInfo(technique) : null;

  const handleMouseEnter = () => {
    if (showTooltip && !('ontouchstart' in window)) {
      setIsTooltipVisible(true);
    }
  };

  const handleMouseLeave = () => {
    setIsTooltipVisible(false);
  };

  const handleClick = (e) => {
    e.stopPropagation();
    if (showTooltip) {
      setIsTooltipVisible(!isTooltipVisible);
    }
  };

  const sizeClass = `neuro-badge--${size}`;
  const variantClass = `neuro-badge--${variant}`;

  return (
    <span
      className={`neuro-badge ${sizeClass} ${variantClass} ${className}`}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      onClick={handleClick}
      role="status"
      aria-label={technique ? `Neuro-informed technique: ${technique}` : 'LRB Neuro-Informed'}
    >
      <span className="neuro-badge__icon" aria-hidden="true">
        {/* Brain emoji as icon */}
        <svg
          viewBox="0 0 24 24"
          fill="currentColor"
          className="neuro-badge__brain-icon"
        >
          <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-1 17.93c-3.95-.49-7-3.85-7-7.93 0-.62.08-1.21.21-1.79L9 15v1c0 1.1.9 2 2 2v1.93zm6.9-2.54c-.26-.81-1-1.39-1.9-1.39h-1v-3c0-.55-.45-1-1-1H8v-2h2c.55 0 1-.45 1-1V7h2c1.1 0 2-.9 2-2v-.41c2.93 1.19 5 4.06 5 7.41 0 2.08-.8 3.97-2.1 5.39z"/>
        </svg>
      </span>

      <span className="neuro-badge__text">
        {technique ? (
          <>
            <span className="neuro-badge__label">LRB</span>
            <span className="neuro-badge__separator">•</span>
            <span className="neuro-badge__technique">{technique}</span>
          </>
        ) : (
          <span className="neuro-badge__label">LRB Neuro-Informed</span>
        )}
      </span>

      {showTooltip && isTooltipVisible && (
        <span className="neuro-badge__tooltip">
          <span className="neuro-badge__tooltip-title">
            {technique || 'Neuro-Informed Approach'}
          </span>
          <span className="neuro-badge__tooltip-desc">
            {techniqueInfo?.description ||
              'This content applies brain-friendly teaching strategies backed by learning science research.'}
          </span>
        </span>
      )}
    </span>
  );
};

/**
 * NeuroTechniqueList Component
 *
 * Displays a list of neuro-techniques applied (for session plans, summaries)
 *
 * Usage:
 * <NeuroTechniqueList techniques={['Spaced Retrieval', 'Chunking']} />
 */
export const NeuroTechniqueList = ({ techniques = [], className = '' }) => {
  if (!techniques || techniques.length === 0) {
    return null;
  }

  return (
    <div className={`neuro-technique-list ${className}`}>
      <span className="neuro-technique-list__label">Techniques Applied:</span>
      <div className="neuro-technique-list__badges">
        {techniques.map((technique, index) => (
          <NeuroInformedBadge
            key={index}
            technique={technique}
            size="small"
            variant="subtle"
          />
        ))}
      </div>
    </div>
  );
};

export default NeuroInformedBadge;
